if (!customElements.get('image-zoom')) {
  class ImageZoom extends HTMLElement {
    constructor() {
      super();

      this.image = this.querySelector('img');

      this.minScale = 1;
      this.maxScale = 64;
      this.imgWidth = null;
      this.imgHeight = null;
      this.viewportWidth = null;
      this.viewportHeight = null;
      this.scale = null;
      this.lastScale = null;
      this.img = null;
      this.x = 0;
      this.lastX = 0;
      this.y = 0;
      this.lastY = 0;
      this.pinchCenter = null;
    }

    connectedCallback() {
      if (this.isTouchDevice()) {
        if (this.image.complete) {
          this.init();
        } else {
          this.image.addEventListener('load', this.init.bind(this));
        }
      }
    }

    init() {
      this.disableImgEventHandlers();

      this.imgWidth = this.image.width;
      this.imgHeight = this.image.height;
      this.viewportWidth = this.image.offsetWidth;
      this.scale = this.viewportWidth / this.imgWidth;
      this.lastScale = this.scale;
      this.viewportHeight = this.offsetHeight;
      this.curWidth = this.imgWidth * this.scale;
      this.curHeight = this.imgHeight * this.scale;

      this.attachEvents();
    }

    attachEvents() {
      const thisInstance = this;

      const hammer = new Hammer(this, {
        domEvents: true
      });

      hammer.get('pinch').set({
        enable: true
      });

      hammer.on('pan', function (event) {
        thisInstance.translate(event.deltaX, event.deltaY);
      });

      hammer.on('panend', () => thisInstance.updateLastPos());

      hammer.on('pinch', function (event) {
        let pinchCenterOffset = { x: 0, y: 0 };

        if (thisInstance.pinchCenter === null) {
          thisInstance.pinchCenter = thisInstance.rawCenter(event);

          const offsetX = thisInstance.pinchCenter.x * thisInstance.scale - (-thisInstance.x * thisInstance.scale + Math.min(thisInstance.viewportWidth, thisInstance.curWidth) / 2);
          const offsetY = thisInstance.pinchCenter.y * thisInstance.scale - (-thisInstance.y * thisInstance.scale + Math.min(thisInstance.viewportHeight, thisInstance.curHeight) / 2);

          pinchCenterOffset = { x: offsetX, y: offsetY };
        }

        let newScale = thisInstance.restrictScale(thisInstance.scale * event.scale);

        let zoomX = thisInstance.pinchCenter.x * newScale - pinchCenterOffset.x;
        let zoomY = thisInstance.pinchCenter.y * newScale - pinchCenterOffset.y;
        let zoomCenter = { x: zoomX / newScale, y: zoomY / newScale };

        thisInstance.zoomAround(event.scale, zoomCenter.x, zoomCenter.y, true);

        document.dispatchEvent(new CustomEvent('alexa:start-mobile-zoom'));
      });

      hammer.on('pinchend', function (event) {
        thisInstance.updateLastScale();
        thisInstance.updateLastPos();
        thisInstance.pinchCenter = null;

        if (event.scale < thisInstance.minScale) {
          document.dispatchEvent(new CustomEvent('alexa:end-mobile-zoom'));
        }
      });

      hammer.on('doubletap', function (event) {
        const center = thisInstance.rawCenter(event);

        thisInstance.zoomAround(2, center.x, center.y);

        document.dispatchEvent(new CustomEvent('alexa:start-mobile-zoom'));
      });
    }

    // We need to disable the following event handlers so that the browser doesn't try to
    // automatically handle our image drag gestures.
    disableImgEventHandlers() {
      const imageElement = this.image;
      const events = ['onclick', 'onmousedown', 'onmousemove', 'onmouseout', 'onmouseover', 'onmouseup', 'ondblclick', 'onfocus', 'onblur'];

      events.forEach(function (event) {
        imageElement[event] = function () {
          return false;
        };
      });
    };

    // Traverse the DOM to calculate the absolute position of an element
    absolutePosition(element) {
      let x = 0;
      let y = 0;

      while (element !== null) {
        x += element.offsetLeft;
        y += element.offsetTop;
        element = element.offsetParent;
      }

      return { x: x, y: y };
    };

    restrictScale(scale) {
      if (scale < this.minScale) {
        scale = this.minScale;
      } else if (scale > this.maxScale) {
        scale = this.maxScale;
      }
      return scale;
    };

    restrictRawPos(pos, viewportDim, imgDim) {
      if (pos < viewportDim / this.scale - imgDim) {
        pos = viewportDim / this.scale - imgDim;
      } else if (pos > 0) {
        pos = 0;
      }
      return pos;
    };

    updateLastPos(deltaX, deltaY) {
      this.lastX = this.x;
      this.lastY = this.y;
    };

    translate(deltaX, deltaY) {
      let newX = this.restrictRawPos(this.lastX + deltaX / this.scale, Math.min(this.viewportWidth, this.curWidth), this.imgWidth);
      this.x = newX;

      this.image.style.marginLeft = Math.ceil(newX * this.scale) + 'px';

      let newY = this.restrictRawPos(this.lastY + deltaY / this.scale, Math.min(this.viewportHeight, this.curHeight), this.imgHeight);
      this.y = newY;

      this.image.style.marginTop = Math.ceil(newY * this.scale) + 'px';
    };

    zoom(scaleBy) {
      this.scale = this.restrictScale(this.lastScale * scaleBy);

      this.curWidth = this.imgWidth * this.scale;
      this.curHeight = this.imgHeight * this.scale;

      this.image.style.width = Math.ceil(this.curWidth) + 'px';
      this.image.style.height = Math.ceil(this.curHeight) + 'px';

      this.translate(0, 0);
    };

    rawCenter(event) {
      var pos = this.absolutePosition(this);

      var scrollLeft = window.scrollX ? window.scrollX : document.body.scrollLeft;
      var scrollTop = window.scrollY ? window.scrollY : document.body.scrollTop;

      var zoomX = -this.x + (event.center.x - pos.x + scrollLeft) / this.scale;
      var zoomY = -this.y + (event.center.y - pos.y + scrollTop) / this.scale;

      return { x: zoomX, y: zoomY };
    };

    updateLastScale() {
      this.lastScale = this.scale;
    };

    zoomAround(scaleBy, rawZoomX, rawZoomY, doNotUpdateLast) {
      // Zoom
      this.zoom(scaleBy);

      // New raw center of viewport
      let rawCenterX = -this.x + Math.min(this.viewportWidth, this.curWidth) / 2 / this.scale;
      let rawCenterY = -this.y + Math.min(this.viewportHeight, this.curHeight) / 2 / this.scale;

      // Delta
      let deltaX = (rawCenterX - rawZoomX) * this.scale;
      let deltaY = (rawCenterY - rawZoomY) * this.scale;

      // Translate back to zoom center
      this.translate(deltaX, deltaY);

      if (!doNotUpdateLast) {
        this.updateLastScale();
        this.updateLastPos();
      }
    };

    isTouchDevice() {
      return (('ontouchstart' in window) ||
         (navigator.maxTouchPoints > 0) ||
         (navigator.msMaxTouchPoints > 0));
    }
  }

  customElements.define('image-zoom', ImageZoom);
}
